/**
 * portfolioService.js — API layer for Portfolio Tracking module.
 *
 * All broker calls are proxied through the backend; the frontend
 * never contacts broker APIs directly.
 */

import { api } from '../auth/apiClient';

// ────────────────────────────── Broker Connections ──────────────────────────────

export async function listBrokerConnections() {
    return api.get('/portfolio/brokers');
}

export async function connectBroker(broker, credentials) {
    return api.post('/portfolio/brokers/connect', { broker, credentials });
}

export async function disconnectBroker(broker) {
    return api.delete(`/portfolio/brokers/${broker}`);
}

export async function refreshBrokerSession(broker, sessionData) {
    return api.post(`/portfolio/brokers/${broker}/refresh-session`, sessionData);
}

export async function getBrokerHealth(broker) {
    return api.get(`/portfolio/brokers/${broker}/health`);
}

// ────────────────────────────── Sync ──────────────────────────────

export async function triggerSync(broker = null) {
    const params = broker ? `?broker=${broker}` : '';
    return api.post(`/portfolio/sync${params}`);
}

export async function getSyncStatus() {
    return api.get('/portfolio/sync/status');
}

// ────────────────────────────── Overview ──────────────────────────────

export async function getPortfolioOverview(lane = null) {
    const params = lane ? `?lane=${lane}` : '';
    return api.get(`/portfolio/overview${params}`);
}

export async function getPortfolioXIRR(lane = null) {
    const params = lane ? `?lane=${lane}` : '';
    return api.get(`/portfolio/xirr${params}`);
}

export async function getAllocation(lane = null) {
    const params = lane ? `?lane=${lane}` : '';
    return api.get(`/portfolio/allocation${params}`);
}

export async function getRiskSummary(period = '1Y', lane = null) {
    const params = new URLSearchParams({ period });
    if (lane) params.set('lane', lane);
    return api.get(`/portfolio/risk?${params.toString()}`);
}

export async function getSectorPerformance(sector, period = '1Y') {
    return api.get(`/portfolio/sector-performance?sector=${encodeURIComponent(sector)}&period=${period}`);
}

// ────────────────────────────── Holdings ──────────────────────────────

export async function getHoldings(lane = null) {
    const params = lane ? `?lane=${lane}` : '';
    return api.get(`/portfolio/holdings${params}`);
}

export async function getHoldingDetail(instrumentId) {
    return api.get(`/portfolio/holdings/${encodeURIComponent(instrumentId)}`);
}

export async function getHoldingXIRR(instrumentId) {
    return api.get(`/portfolio/holdings/${encodeURIComponent(instrumentId)}/xirr`);
}

export async function getHoldingNews(instrumentId) {
    return api.get(`/portfolio/holdings/${encodeURIComponent(instrumentId)}/news`);
}

// ────────────────────────────── Transactions (Ledger) ──────────────────────────────

export async function getTransactions({ broker, instrument, dateFrom, dateTo, type, page = 1, limit = 50 } = {}) {
    const params = new URLSearchParams();
    if (broker) params.set('broker', broker);
    if (instrument) params.set('instrument', instrument);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (type) params.set('type', type);
    params.set('page', page);
    params.set('limit', limit);
    return api.get(`/portfolio/transactions?${params.toString()}`);
}

export async function exportTransactionsCSV(filters = {}) {
    const params = new URLSearchParams(filters);
    // Returns a blob URL for download
    const response = await api.get(`/portfolio/transactions/export?${params.toString()}`, {
        responseType: 'blob',
    });
    return response;
}

export async function importTransactionsCSV(file, broker) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('broker', broker);
    return api.post('/portfolio/transactions/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
}

// ────────────────────────────── News & Events ──────────────────────────────

export async function getPortfolioNews({ page = 1, limit = 20 } = {}) {
    return api.get(`/portfolio/news?page=${page}&limit=${limit}`);
}

export async function markNewsRead(newsId) {
    return api.patch(`/portfolio/news/${newsId}/read`);
}

export async function getEarningsCalendar() {
    return api.get('/portfolio/events/earnings');
}

export async function getDividendEvents() {
    return api.get('/portfolio/events/dividends');
}

// ────────────────────────────── Alerts ──────────────────────────────

export async function getAlerts() {
    return api.get('/portfolio/alerts');
}

export async function createAlert(alertConfig) {
    return api.post('/portfolio/alerts', alertConfig);
}

export async function updateAlert(alertId, alertConfig) {
    return api.put(`/portfolio/alerts/${alertId}`, alertConfig);
}

export async function deleteAlert(alertId) {
    return api.delete(`/portfolio/alerts/${alertId}`);
}

// ────────────────────────────── Settings ──────────────────────────────

export async function getPortfolioSettings() {
    return api.get('/portfolio/settings');
}

export async function updatePortfolioSettings(settings) {
    return api.put('/portfolio/settings', settings);
}

export async function setTargetAllocation(allocation) {
    return api.put('/portfolio/settings/target-allocation', { allocation });
}

// ────────────────────────────── Asset-Class Allocation ──────────────────────────────

export async function getAssetClassAllocation() {
    return api.get('/portfolio/allocation?mode=assetClass');
}

// ────────────────────────────── Drift & Rebalancing ──────────────────────────────

export async function getDrift() {
    return api.get('/portfolio/drift');
}

export async function getRebalancingSuggestions() {
    return api.get('/portfolio/rebalancing');
}

// ────────────────────────────── Concentration ──────────────────────────────

export async function getConcentrationWarnings() {
    return api.get('/portfolio/concentration');
}

// ────────────────────────────── SIP Consistency ──────────────────────────────

export async function getSipConsistency(months = 12) {
    return api.get(`/portfolio/sip/consistency?months=${months}`);
}

// ────────────────────────────── MF Import ──────────────────────────────

export async function importMFHoldings(holdings) {
    return api.post('/portfolio/mf/import', { holdings });
}
